import * as XLSX from "xlsx";
import {
    CheckCircleFilled,
    CheckCircleOutlined,
    CloseCircleFilled,
    DatabaseOutlined,
    DownloadOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    FileExcelOutlined,
    InfoCircleOutlined,
    InboxOutlined,
    LoadingOutlined,
    RocketOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Modal,
    Progress,
    Row,
    Select,
    Space,
    Statistic,
    Steps,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PageLayout from "../../components/PageLayout";
import axiosInstance from "../../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../../variables/api/axiosMultiPartDatainstance";

const { Title, Text } = Typography;

// ─── Labels ────────────────────────────────────────────────────────────────

const FORMAT_LABELS = {
    EFORCE: { label: "eFORCE", color: "blue", desc: ".xls · toutes compagnies" },
    EFORCE_PAYES: { label: "eFORCE", color: "blue", desc: ".xls · toutes compagnies" },
    BSP: { label: "BSP SANLAM", color: "orange", desc: ".xlsx · Sinistres Payés" },
    TEMPLATE: { label: "Template FIDELIA", color: "green", desc: ".xlsx · format standard" },
    TEMPLATE_PAYES: { label: "Template FIDELIA", color: "green", desc: ".xlsx · format standard" },
};

const MODE_LABELS = {
    CHEQUE: "Chèque", ESPECE: "Espèces", VIREMENT: "Virement",
    MOBILE_MONEY: "Mobile Money", COMPENSATION: "Compensation",
};
const MODE_COLORS = {
    CHEQUE: "blue", ESPECE: "green", VIREMENT: "geekblue",
    MOBILE_MONEY: "cyan", COMPENSATION: "purple",
};

// ─── Helpers ───────────────────────────────────────────────────────────────

const formatMontant = (v) =>
    v == null ? "—" : new Intl.NumberFormat("fr-FR").format(Math.round(v)) + " FCFA";

const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d) ? String(iso) : d.toLocaleDateString("fr-FR");
};

// ─── Colonnes preview paiements ─────────────────────────────────────────────

const previewPaieColumns = [
    {
        title: "", key: "s", width: 32, fixed: "left",
        render: (_, r) => {
            if (r._dejaImporte)
                return <Tooltip title="Déjà présent en base — ignoré à l'import"><DatabaseOutlined style={{ color: "#9333ea" }} /></Tooltip>;
            if (r._avertissement)
                return <Tooltip title={r._avertissement}><WarningOutlined style={{ color: "#f97316" }} /></Tooltip>;
            if (r._info)
                return <Tooltip title={r._info}><InfoCircleOutlined style={{ color: "#1d4ed8" }} /></Tooltip>;
            return <CheckCircleOutlined style={{ color: "#1a6b3c" }} />;
        },
    },
    {
        title: "Ligne", dataIndex: "ligne", key: "ligne", width: 60,
        render: v => <Text type="secondary" style={{ fontSize: 11 }}>L{v ?? "?"}</Text>
    },
    {
        title: "N° Sinistre", dataIndex: "sinistre", key: "sinistre", width: 155, fixed: "left",
        render: v => <Text code style={{ fontSize: 11, fontWeight: 700 }}>{v || "—"}</Text>
    },
    {
        title: "N° Police", dataIndex: "police", key: "police", width: 130,
        render: v => <Text code style={{ fontSize: 11 }}>{v || "—"}</Text>
    },
    {
        title: "Immatriculation", dataIndex: "immat", key: "immat", width: 130,
        render: v => v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : <Text type="secondary">—</Text>
    },
    {
        title: "Montant", dataIndex: "montant", key: "montant", width: 140, align: "right",
        render: v => <Text strong style={{ fontSize: 12, color: "#166534" }}>
            {v != null ? new Intl.NumberFormat("fr-FR").format(Math.round(v)) : "—"}
        </Text>
    },
    {
        title: "Date règlement", dataIndex: "date", key: "date", width: 120,
        render: v => v ? <Text style={{ fontSize: 11 }}>{formatDate(v)}</Text>
            : <Tag color="red" style={{ fontSize: 10 }}>Manquant</Tag>
    },
    {
        title: "Mode", dataIndex: "mode", key: "mode", width: 120,
        render: v => v ? <Tag color={MODE_COLORS[v] || "default"} style={{ fontSize: 10 }}>
            {MODE_LABELS[v] || v}
        </Tag> : <Text type="secondary">—</Text>
    },
    {
        title: "Compagnie", dataIndex: "compagnie", key: "compagnie", width: 100,
        render: v => v ? <Tag color="geekblue" style={{ fontSize: 10 }}>{v}</Tag> : <Text type="secondary">—</Text>
    },
];

// ─── Colonnes erreurs rapport ───────────────────────────────────────────────

const erreurColumns = [
    {
        title: "Ligne", dataIndex: "ligne", key: "ligne", width: 60,
        render: v => <Text type="secondary">{v ?? "—"}</Text>
    },
    {
        title: "N° Sinistre", dataIndex: "sinistre", key: "sinistre", width: 155,
        render: v => <Text code style={{ fontSize: 12 }}>{v || "—"}</Text>
    },
    {
        title: "N° Police", dataIndex: "police", key: "police", width: 130,
        render: v => <Text code style={{ fontSize: 12 }}>{v || "—"}</Text>
    },
    {
        title: "Immat.", dataIndex: "immatriculation", key: "immat", width: 120,
        render: v => v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : <Text type="secondary">—</Text>
    },
    {
        title: "Compagnie", dataIndex: "compagnie", key: "compagnie", width: 100,
        render: v => v ? <Tag color="geekblue" style={{ fontSize: 10 }}>{v}</Tag> : <Text type="secondary">—</Text>
    },
    {
        title: "Montant", dataIndex: "montant", key: "montant", width: 130, align: "right",
        render: v => v != null ? <Text>{new Intl.NumberFormat("fr-FR").format(Math.round(v))}</Text> : <Text type="secondary">—</Text>
    },
    {
        title: "Date règlement", dataIndex: "dateReglement", key: "dateReglement", width: 120,
        render: v => v ? <Text style={{ fontSize: 11 }}>{formatDate(v)}</Text> : <Text type="secondary">—</Text>
    },
    {
        title: "Cause du rejet", dataIndex: "erreur", key: "erreur",
        render: v => <Text type="danger" style={{ fontSize: 12 }}>{v || "—"}</Text>
    },
];

// ─── Carte de prévisualisation ──────────────────────────────────────────────

const PreviewCard = ({ preview }) => {
    if (!preview.ok) {
        return (
            <Alert type="error" showIcon
                message={`Erreur — ${preview.fichier}`}
                description={preview.messageErreur}
                style={{ marginBottom: 12 }}
            />
        );
    }

    const fmt = FORMAT_LABELS[preview.formatDetecte] || {};
    const repCompagnie = preview.repartitionCompagnie || {};

    return (
        <Card
            size="small"
            style={{ marginBottom: 16, borderLeft: "4px solid #242166" }}
            title={
                <Space wrap>
                    <FileExcelOutlined style={{ color: "#217346" }} />
                    <Text strong>{preview.fichier}</Text>
                    {fmt.label && <Tag color={fmt.color}>{fmt.label}</Tag>}
                    {fmt.desc && <Text type="secondary" style={{ fontSize: 11 }}>{fmt.desc}</Text>}
                    <Tag color="default">{(preview.dureeParsingMs / 1000).toFixed(2)}s</Tag>
                </Space>
            }
        >
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}>
                    <Statistic title="Paiements parsés" value={preview.totalSinistres}
                        valueStyle={{ color: "#242166", fontSize: 22 }} />
                </Col>
                <Col xs={12} sm={6}>
                    <Statistic title="Sinistres trouvés" value={preview.totalLiesTrouves}
                        valueStyle={{ color: "#52c41a", fontSize: 22 }} />
                </Col>
                <Col xs={12} sm={6}>
                    <Statistic title="Sinistres manquants" value={preview.totalLiesManquants}
                        valueStyle={{ color: preview.totalLiesManquants > 0 ? "#ff4d4f" : "#52c41a", fontSize: 22 }} />
                </Col>
                <Col xs={12} sm={6}>
                    <Statistic title="Total paiements"
                        formatter={() => (
                            <Text strong style={{ fontSize: 16, color: "#166534" }}>
                                {formatMontant(preview.sommePaiements)}
                            </Text>
                        )}
                    />
                </Col>
            </Row>

            {preview.totalDejaImportes > 0 && (
                <Alert
                    type="info" showIcon
                    icon={<DatabaseOutlined style={{ color: "#9333ea" }} />}
                    style={{ marginBottom: 12 }}
                    message={
                        <span>
                            <Text strong style={{ color: "#7e22ce" }}>{preview.totalDejaImportes}</Text>
                            {" "}paiement(s) déjà en base — ignoré(s) à l'import (badge violet sur les lignes).
                        </span>
                    }
                />
            )}

            <Divider style={{ margin: "10px 0" }} />

            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
                        Répartition par compagnie
                    </Text>
                    <Space wrap>
                        {Object.entries(repCompagnie).map(([k, v]) => (
                            <Tag key={k} color="geekblue" style={{ fontSize: 10 }}>
                                {k} : <strong>{v}</strong>
                            </Tag>
                        ))}
                        {Object.keys(repCompagnie).length === 0 && <Text type="secondary">—</Text>}
                    </Space>
                </Col>
                <Col xs={24} md={12}>
                    <Descriptions size="small" column={1} bordered={false}>
                        <Descriptions.Item label="Somme des paiements">
                            <Text strong style={{ color: "#166534" }}>
                                {formatMontant(preview.sommePaiements)}
                            </Text>
                        </Descriptions.Item>
                    </Descriptions>
                </Col>
            </Row>
        </Card>
    );
};

// ─── Page principale ────────────────────────────────────────────────────────

const RepriseSinistresPaie = () => {
    const [compagnies, setCompagnies] = useState([]);
    const [selectedCompagnie, setSelectedCompagnie] = useState(null);
    const [fichiers, setFichiers] = useState([]);

    const [step, setStep] = useState(0);

    const [previews, setPreviews] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);

    const [rapports, setRapports] = useState([]);
    const [importLoading, setImportLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentFile, setCurrentFile] = useState(null);

    const [forceModalVisible, setForceModalVisible] = useState(false);
    const [forceModalStep, setForceModalStep] = useState(1);

    const abortRef = useRef(false);

    useEffect(() => {
        axiosInstance.get("/api/organisme/assurance")
            .then(res => setCompagnies(res.data))
            .catch(() => toast.error("Impossible de charger les compagnies"));
    }, []);

    const handleUploadChange = ({ fileList }) => setFichiers(fileList);

    // ── Étape 0 → 1 : Prévisualiser ─────────────────────────────────────────
    const lancerPreview = async () => {
        const valides = fichiers.filter(f => f.originFileObj);
        if (valides.length === 0) { toast.warn("Ajoutez au moins un fichier"); return; }

        setPreviewLoading(true);
        setPreviews([]);
        const resultats = [];

        for (const f of valides) {
            const formData = new FormData();
            formData.append("fichier", f.originFileObj);
            if (selectedCompagnie) formData.append("compagnie", selectedCompagnie.code);

            try {
                const res = await axiosMultiPartDataInstance.post(
                    "/api/migration/reprise-sinistre-paiement/preview", formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
                resultats.push(res.data);
            } catch (err) {
                const msg = err.response?.data?.message || err.message || "Erreur serveur";
                resultats.push({ fichier: f.name, ok: false, messageErreur: msg });
            }
        }

        setPreviews(resultats);
        setPreviewLoading(false);
        setStep(1);

        const nbErreurs = resultats.filter(r => !r.ok).length;
        if (nbErreurs > 0)
            toast.warn(`${nbErreurs} fichier(s) en erreur — vérifiez l'aperçu`);
        else if (resultats.some(r => r.totalLiesManquants > 0))
            toast.warn("Des paiements n'ont pas de sinistre déclaré associé en base");
        else
            toast.success("Aperçu généré — vérifiez avant d'importer");
    };

    // ── Étape 1 → 2 → 3 : Importer (normal ou force) ────────────────────────
    const lancerImport = async (forceImport = false) => {
        const valides = fichiers.filter(f => f.originFileObj);
        if (valides.length === 0) return;

        setForceModalVisible(false);
        setForceModalStep(1);
        setImportLoading(true);
        abortRef.current = false;
        setRapports([]);
        setProgress(0);
        setStep(2);

        const resultats = [];
        for (let i = 0; i < valides.length; i++) {
            if (abortRef.current) break;
            const f = valides[i];
            setCurrentFile(f.name);

            const formData = new FormData();
            formData.append("fichier", f.originFileObj);
            if (selectedCompagnie) formData.append("compagnie", selectedCompagnie.code);
            if (forceImport) formData.append("forceImport", "true");

            try {
                const res = await axiosMultiPartDataInstance.post(
                    "/api/migration/reprise-sinistre-paiement", formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
                resultats.push(res.data);
            } catch (err) {
                resultats.push({
                    fichier: f.name, ok: false,
                    messageErreurGlobal: err.response?.data?.message || err.message || "Erreur serveur",
                    totalParse: 0, succes: 0, dejaExistants: 0, forcesCreations: 0, erreurs: [],
                });
            }
            setProgress(Math.round(((i + 1) / valides.length) * 100));
        }

        setRapports(resultats);
        setImportLoading(false);
        setCurrentFile(null);
        setStep(3);

        const totalSucces  = resultats.reduce((s, r) => s + (r.succes || 0), 0);
        const totalForces  = resultats.reduce((s, r) => s + (r.forcesCreations || 0), 0);
        const totalErreurs = resultats.reduce((s, r) => s + (r.erreurs?.length || 0), 0);
        if (forceImport && totalForces > 0)
            toast.warn(`Import force terminé — ${totalSucces} intégrés dont ${totalForces} force-créés, ${totalErreurs} rejetés`);
        else
            toast.success(`Import terminé — ${totalSucces} paiements intégrés, ${totalErreurs} rejetés`);
    };

    const exporterRapport = () => {
        if (rapports.length === 0) return;
        const wb = XLSX.utils.book_new();
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10);

        const resumeRows = [["Fichier", "Parsés", "Intégrés", "Force-créés", "Déjà existants", "Rejetés", "Durée (s)", "Compagnie"]];
        rapports.forEach(r => resumeRows.push([
            r.fichier,
            r.totalParse,
            r.succes,
            r.forcesCreations || 0,
            r.dejaExistants || 0,
            (r.erreurs || []).length,
            r.dureeMs != null ? (r.dureeMs / 1000).toFixed(1) : "—",
            selectedCompagnie?.code || r.codeCompagnie || "—",
        ]));
        const wsResume = XLSX.utils.aoa_to_sheet(resumeRows);
        wsResume["!cols"] = [{ wch: 40 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 10 }, { wch: 12 }, { wch: 14 }];
        XLSX.utils.book_append_sheet(wb, wsResume, "Résumé");

        const erreursRows = [["Fichier", "Ligne", "N° Sinistre", "N° Police", "Immatriculation", "Compagnie", "Montant", "Date règlement", "Cause du rejet"]];
        rapports.forEach(r => {
            (r.erreurs || []).forEach(e => erreursRows.push([
                r.fichier,
                e.ligne ?? "",
                e.sinistre || "",
                e.police || "",
                e.immatriculation || "",
                e.compagnie || "",
                e.montant != null ? Math.round(e.montant) : "",
                e.dateReglement || "",
                e.erreur || "",
            ]));
        });
        const wsErreurs = XLSX.utils.aoa_to_sheet(erreursRows);
        wsErreurs["!cols"] = [{ wch: 35 }, { wch: 8 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 60 }];
        XLSX.utils.book_append_sheet(wb, wsErreurs, "Lignes rejetées");

        const compCode = (selectedCompagnie?.code || "").toUpperCase().replace(/[^A-Z0-9]/g, "_");
        XLSX.writeFile(wb, `REPRISE_SINISTRES_PAIE_${compCode}_${dateStr}.xlsx`);
    };

    const resetAll = () => {
        setStep(0); setFichiers([]); setPreviews([]); setRapports([]);
        setProgress(0); setCurrentFile(null);
        setForceModalVisible(false); setForceModalStep(1);
    };

    // ── Agrégats globaux preview ──────────────────────────────────────────────
    const totalPaieGlobal      = previews.filter(p => p.ok).reduce((s, p) => s + (p.totalSinistres    || 0), 0);
    const totalTrouvesGlobal   = previews.filter(p => p.ok).reduce((s, p) => s + (p.totalLiesTrouves  || 0), 0);
    const totalManquantsGlobal = previews.filter(p => p.ok).reduce((s, p) => s + (p.totalLiesManquants || 0), 0);
    const sommePaieGlobal      = previews.filter(p => p.ok).reduce((s, p) => s + (p.sommePaiements    || 0), 0);

    // ── Agrégats globaux rapport ──────────────────────────────────────────────
    const totalParseGlobal  = rapports.reduce((s, r) => s + (r.totalParse        || 0), 0);
    const totalSuccesGlobal = rapports.reduce((s, r) => s + (r.succes            || 0), 0);
    const totalForcesGlobal = rapports.reduce((s, r) => s + (r.forcesCreations   || 0), 0);
    const totalDejaGlobal   = rapports.reduce((s, r) => s + (r.dejaExistants     || 0), 0);
    const totalRejetsGlobal = rapports.reduce((s, r) => s + (r.erreurs?.length   || 0), 0);

    const nbFichiersValides = fichiers.filter(f => f.originFileObj).length;

    return (
        <PageLayout currentMenu="REPRISE">
            <div className="container-xxl flex-grow-1 container-p-y">

                {/* En-tête */}
                <div className="mb-4">
                    <Title level={4} style={{ margin: 0, color: "#242166" }}>
                        <DatabaseOutlined className="me-2" />
                        Reprise — Sinistres Payés
                    </Title>
                    <Text type="secondary">
                        Formats acceptés : eFORCE (.xls) · BSP SANLAM (.xlsx) · Template FIDELIA (.xlsx)
                    </Text>
                </div>

                {/* Stepper */}
                <Card style={{ marginBottom: 24 }}>
                    <Steps current={step} size="small" items={[
                        { title: "Sélection", icon: <FileExcelOutlined /> },
                        { title: "Aperçu",    icon: previewLoading ? <LoadingOutlined /> : <EyeOutlined /> },
                        { title: "Import",    icon: importLoading  ? <LoadingOutlined /> : <RocketOutlined /> },
                        { title: "Rapport",   icon: step === 3 ? <CheckCircleFilled style={{ color: "#52c41a" }} /> : <DatabaseOutlined /> },
                    ]} />
                </Card>

                {/* ── ÉTAPE 0 : Sélection ─────────────────────────────────── */}
                {step === 0 && (
                    <Card title="Paramètres d'import" style={{ borderTop: "3px solid #242166" }}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={10}>
                                <div className="mb-2">
                                    <Text strong>Compagnie</Text>
                                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                                        (optionnel pour eFORCE — lue depuis le fichier)
                                    </Text>
                                </div>
                                <Select
                                    placeholder="Sélectionner la compagnie (optionnel)"
                                    size="large"
                                    style={{ width: "100%" }}
                                    showSearch allowClear
                                    filterOption={(input, opt) =>
                                        opt.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={(_, opt) => setSelectedCompagnie(opt?.data || null)}
                                    options={compagnies.map(c => ({ value: c.code, label: c.raisonSociale, data: c }))}
                                />
                                <Text style={{ fontSize: 11, color: "#1d4ed8", display: "block", marginTop: 4 }}>
                                    Pour BSP SANLAM, la compagnie sélectionnée est appliquée à toutes les lignes.
                                    La date de règlement est extraite du nom du fichier (ex: BSP SAHAM TPV 012025.xlsx → 31/01/2025).
                                </Text>
                            </Col>

                            <Col xs={24} md={14}>
                                <div className="mb-2">
                                    <Text strong>Fichiers sinistres payés</Text>
                                    <Text type="secondary" className="ms-2">(plusieurs fichiers acceptés)</Text>
                                    <Tag color="default" className="ms-2" style={{ fontSize: 11 }}>
                                        Format détecté automatiquement
                                    </Tag>
                                </div>
                                <Dragger
                                    onChange={handleUploadChange}
                                    beforeUpload={() => false}
                                    accept=".xls,.xlsx"
                                    multiple
                                    fileList={fichiers}
                                >
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined style={{ fontSize: 36, color: "#242166" }} />
                                    </p>
                                    <p className="ant-upload-text">Glissez les fichiers ici ou cliquez</p>
                                    <p className="ant-upload-hint text-muted">
                                        eFORCE SINISTRES_PAYES.XLS · BSP SANLAM .xlsx · Template FIDELIA .xlsx — plusieurs fichiers acceptés
                                    </p>
                                </Dragger>
                            </Col>
                        </Row>

                        <Alert
                            type="info" showIcon
                            message="Prérequis : les sinistres déclarés doivent être importés avant les paiements"
                            description="Chaque paiement est rattaché à un sinistre déclaré existant en base. Si le numéro de sinistre n'existe pas, le paiement est rejeté."
                            style={{ marginTop: 16 }}
                        />

                        <Divider orientation="left" style={{ marginTop: 24 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Formats supportés</Text>
                        </Divider>
                        <Space wrap>
                            <Tag color="blue"   style={{ fontSize: 11 }}>eFORCE · SINISTRES_PAYES.XLS · Compagnie dans le fichier</Tag>
                            <Tag color="orange" style={{ fontSize: 11 }}>BSP SANLAM · .xlsx · Date extraite du nom du fichier · MMYYYY</Tag>
                            <Tag color="green"  style={{ fontSize: 11 }}>Template FIDELIA · .xlsx · feuille A · col Montant + Mode</Tag>
                        </Space>

                        <div className="d-flex justify-content-end mt-4">
                            <Button
                                type="primary"
                                size="large"
                                icon={<EyeOutlined />}
                                loading={previewLoading}
                                onClick={lancerPreview}
                                style={{ backgroundColor: "#242166", minWidth: 200 }}
                                disabled={nbFichiersValides === 0}
                            >
                                Prévisualiser{nbFichiersValides > 1 ? ` (${nbFichiersValides} fichiers)` : ""}
                            </Button>
                        </div>
                    </Card>
                )}

                {/* ── ÉTAPE 1 : Aperçu ────────────────────────────────────── */}
                {step === 1 && previews.length > 0 && (
                    <>
                        {/* Synthèse globale */}
                        <Card style={{ marginBottom: 16, borderTop: "3px solid #242166" }}>
                            <Row gutter={16} align="middle">
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Paiements parsés"
                                        value={totalPaieGlobal}
                                        valueStyle={{ color: "#242166", fontSize: 28 }}
                                        suffix={previews.length > 1
                                            ? <Text type="secondary" style={{ fontSize: 13 }}> / {previews.length} fichiers</Text>
                                            : null}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Sinistres trouvés" value={totalTrouvesGlobal}
                                        valueStyle={{ color: "#52c41a", fontSize: 24 }} />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Sinistres manquants" value={totalManquantsGlobal}
                                        valueStyle={{ color: totalManquantsGlobal > 0 ? "#ff4d4f" : "#52c41a", fontSize: 24 }} />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Total à décaisser"
                                        formatter={() => (
                                            <Text strong style={{ fontSize: 16, color: "#166534" }}>
                                                {formatMontant(sommePaieGlobal)}
                                            </Text>
                                        )}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        {/* Détail par fichier */}
                        <Card
                            title={
                                <Space>
                                    <EyeOutlined />
                                    <Text strong>Aperçu par fichier</Text>
                                    <Badge count={previews.length} color="#242166" />
                                </Space>
                            }
                            style={{ marginBottom: 16 }}
                        >
                            {previews.map((preview, idx) => {
                                const lignes           = preview.lignes || [];
                                const nbAvertissements = lignes.filter(l => l._avertissement).length;
                                const nbImmatFallback  = lignes.filter(l => l._info).length;
                                return (
                                    <div key={idx}>
                                        {!preview.ok && (
                                            <Alert type="error" showIcon
                                                message={`Erreur — ${preview.fichier}`}
                                                description={preview.messageErreur}
                                                style={{ marginBottom: 12 }} />
                                        )}
                                        {preview.ok && nbImmatFallback > 0 && (
                                            <Alert type="info" showIcon icon={<InfoCircleOutlined />}
                                                message={`${preview.fichier} — ${nbImmatFallback} paiement(s) rattaché(s) via immatriculation`}
                                                style={{ marginBottom: 8 }} />
                                        )}
                                        {preview.ok && preview.totalLiesManquants > 0 && (
                                            <Alert type="warning" showIcon
                                                message={`${preview.fichier} — ${preview.totalLiesManquants} paiement(s) sans sinistre déclaré associé`}
                                                style={{ marginBottom: 8 }} />
                                        )}
                                        <PreviewCard preview={preview} />
                                        {lignes.length > 0 && (
                                            <Card
                                                size="small"
                                                style={{ marginBottom: 12, padding: 0 }}
                                                bodyStyle={{ padding: 0 }}
                                                title={
                                                    <Space>
                                                        <DatabaseOutlined />
                                                        <Text>Lignes</Text>
                                                        <Tag color="#242166">{preview.totalSinistres} paiements</Tag>
                                                        {nbImmatFallback  > 0 && <Tag color="blue">{nbImmatFallback} via immat</Tag>}
                                                        {nbAvertissements > 0 && (
                                                            <Tag color="orange" icon={<WarningOutlined />}>
                                                                {nbAvertissements} alerte(s)
                                                            </Tag>
                                                        )}
                                                    </Space>
                                                }
                                            >
                                                {nbAvertissements > 0 && (
                                                    <Alert
                                                        type="warning" showIcon
                                                        message={`${nbAvertissements} paiement(s) avec sinistre non trouvé — seront rejetés`}
                                                        style={{ borderRadius: 0, borderLeft: "none", borderRight: "none", borderTop: "none" }}
                                                    />
                                                )}
                                                <Table
                                                    dataSource={lignes.map((r, i) => ({ ...r, key: i }))}
                                                    columns={previewPaieColumns}
                                                    size="small"
                                                    scroll={{ x: 1100, y: 300 }}
                                                    pagination={{
                                                        defaultPageSize: 10,
                                                        pageSizeOptions: [10, 20, 50],
                                                        showSizeChanger: true,
                                                        showTotal: (total, range) =>
                                                            `${range[0]}–${range[1]} sur ${total} paiement${total > 1 ? "s" : ""}`,
                                                    }}
                                                    rowClassName={r => r._avertissement ? "preview-row-warning" : r._info ? "preview-row-immat" : ""}
                                                />
                                            </Card>
                                        )}
                                        {idx < previews.length - 1 && <Divider style={{ margin: "8px 0" }} />}
                                    </div>
                                );
                            })}
                        </Card>

                        {/* Actions */}
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <Button size="large" onClick={() => setStep(0)}>← Modifier</Button>
                            <Space wrap>
                                {totalManquantsGlobal > 0 && (
                                    <Tooltip title="Créer des enregistrements minimaux pour les paiements non résolus — double confirmation requise">
                                        <Button
                                            size="large"
                                            icon={<ThunderboltOutlined />}
                                            onClick={() => { setForceModalStep(1); setForceModalVisible(true); }}
                                            style={{ borderColor: "#faad14", color: "#faad14" }}
                                        >
                                            Importer en mode force ({totalManquantsGlobal} non résolu{totalManquantsGlobal > 1 ? "s" : ""})
                                        </Button>
                                    </Tooltip>
                                )}
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<RocketOutlined />}
                                    onClick={() => lancerImport(false)}
                                    style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", minWidth: 220 }}
                                    disabled={!previews.some(p => p.ok)}
                                >
                                    Confirmer et importer ({previews.filter(p => p.ok).length} fichier{previews.filter(p => p.ok).length > 1 ? "s" : ""})
                                </Button>
                            </Space>
                        </div>
                    </>
                )}

                {/* ── ÉTAPE 2 : Import en cours ───────────────────────────── */}
                {step === 2 && (
                    <Card style={{ borderTop: "3px solid #52c41a" }}>
                        <div style={{ textAlign: "center", padding: "24px 0" }}>
                            <LoadingOutlined style={{ fontSize: 40, color: "#242166", marginBottom: 16 }} />
                            <div>
                                <Text strong style={{ fontSize: 16 }}>Import sinistres payés en cours…</Text>
                            </div>
                            <Text type="secondary">
                                {currentFile
                                    ? <>Fichier : <Text strong>{currentFile}</Text>
                                        &nbsp;({fichiers.filter(f => f.originFileObj).findIndex(f => f.name === currentFile) + 1}/{fichiers.filter(f => f.originFileObj).length})</>
                                    : "Initialisation…"
                                }
                            </Text>
                            <Progress
                                percent={progress}
                                strokeColor="#242166"
                                style={{ marginTop: 16, maxWidth: 500, margin: "16px auto 0" }}
                            />
                        </div>
                    </Card>
                )}

                {/* ── ÉTAPE 3 : Rapport final ──────────────────────────────── */}
                {step === 3 && rapports.length > 0 && (
                    <>
                        {/* Agrégats globaux */}
                        <Card style={{ marginBottom: 16, borderTop: "3px solid #52c41a" }}>
                            <Row gutter={16}>
                                <Col xs={8} sm={4}>
                                    <Statistic title="Parsés" value={totalParseGlobal}
                                        valueStyle={{ color: "#1890ff" }} />
                                </Col>
                                <Col xs={8} sm={5}>
                                    <Statistic title="Intégrés" value={totalSuccesGlobal}
                                        valueStyle={{ color: "#52c41a" }} prefix={<CheckCircleFilled />} />
                                </Col>
                                {totalForcesGlobal > 0 && (
                                    <Col xs={8} sm={5}>
                                        <Statistic title="Force-créés" value={totalForcesGlobal}
                                            valueStyle={{ color: "#faad14" }}
                                            prefix={<ThunderboltOutlined />} />
                                    </Col>
                                )}
                                <Col xs={8} sm={5}>
                                    <Statistic title="Déjà existants" value={totalDejaGlobal}
                                        valueStyle={{ color: "#faad14" }} />
                                </Col>
                                <Col xs={8} sm={5}>
                                    <Statistic
                                        title="Rejetés"
                                        value={totalRejetsGlobal}
                                        valueStyle={{ color: totalRejetsGlobal > 0 ? "#ff4d4f" : "#52c41a" }}
                                        prefix={totalRejetsGlobal > 0 ? <CloseCircleFilled /> : <CheckCircleFilled />}
                                    />
                                </Col>
                            </Row>
                            {totalForcesGlobal > 0 && (
                                <Alert type="warning" showIcon icon={<ThunderboltOutlined />}
                                    message={`${totalForcesGlobal} enregistrement(s) force-créés — marqués [FORCE-REPRISE] — à vérifier et compléter manuellement`}
                                    style={{ marginTop: 16 }} />
                            )}
                        </Card>

                        <Card
                            title={
                                <Space>
                                    <Text strong>Rapport d'import paiements</Text>
                                    <Badge count={rapports.length} color="#242166" />
                                </Space>
                            }
                            extra={
                                <Space>
                                    <Button icon={<DownloadOutlined />} onClick={exporterRapport}>Exporter</Button>
                                    <Button onClick={resetAll}>Nouvelle reprise</Button>
                                </Space>
                            }
                        >
                            {rapports.map((r, idx) => (
                                <div key={idx} style={{ marginBottom: idx < rapports.length - 1 ? 24 : 0 }}>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <Space>
                                            <FileExcelOutlined style={{ color: "#217346" }} />
                                            <Text strong>{r.fichier}</Text>
                                            {r.forceImport && <Tag color="orange" icon={<ThunderboltOutlined />}>Force</Tag>}
                                        </Space>
                                        <Space>
                                            <Tag color="blue">{r.totalParse} parsés</Tag>
                                            <Tag color="green">{r.succes} intégrés</Tag>
                                            {(r.forcesCreations || 0) > 0 && (
                                                <Tag color="orange" icon={<ThunderboltOutlined />}>{r.forcesCreations} force-créés</Tag>
                                            )}
                                            {r.dejaExistants > 0 && (
                                                <Tag color="orange">{r.dejaExistants} déjà existants</Tag>
                                            )}
                                            {(r.erreurs || []).length > 0
                                                ? <Tag color="red">{r.erreurs.length} rejetés</Tag>
                                                : <Tag color="green">Aucun rejet</Tag>
                                            }
                                            {r.dureeMs && <Tag color="default">{(r.dureeMs / 1000).toFixed(1)}s</Tag>}
                                        </Space>
                                    </div>

                                    {!r.ok && r.messageErreurGlobal && (
                                        <Alert type="error" message="Échec global"
                                            description={r.messageErreurGlobal} showIcon style={{ marginBottom: 8 }} />
                                    )}

                                    {(r.erreurs || []).length > 0 && (
                                        <Table
                                            size="small"
                                            columns={erreurColumns}
                                            dataSource={(r.erreurs || []).map((e, i) => ({ ...e, key: i }))}
                                            pagination={{
                                                defaultPageSize: 10,
                                                pageSizeOptions: [10, 20, 50],
                                                showSizeChanger: true,
                                                showTotal: (total, range) =>
                                                    `${range[0]}–${range[1]} sur ${total} rejet${total > 1 ? "s" : ""}`,
                                            }}
                                            scroll={{ x: true }}
                                        />
                                    )}

                                    {idx < rapports.length - 1 && <Divider style={{ margin: "12px 0" }} />}
                                </div>
                            ))}
                        </Card>
                    </>
                )}

            </div>

            {/* ── Modal double confirmation force ────────────────────────── */}
            <Modal
                open={forceModalVisible}
                onCancel={() => { setForceModalVisible(false); setForceModalStep(1); }}
                footer={null}
                width={520}
                closable={forceModalStep === 1}
                maskClosable={forceModalStep === 1}
                title={null}
            >
                {forceModalStep === 1 ? (
                    <>
                        <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
                            <ExclamationCircleOutlined style={{ fontSize: 52, color: "#faad14" }} />
                            <Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>
                                Mode Force — Première confirmation
                            </Title>
                            <Text type="secondary">Lisez attentivement avant de continuer</Text>
                        </div>
                        <Alert type="warning" showIcon
                            message={`${totalManquantsGlobal} paiement(s) seront enregistrés avec données minimales`}
                            description={
                                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                    <li>Pour chaque paiement non résolu, PoolIns créera un sinistre minimal si nécessaire (et un contrat minimal si le contrat est aussi absent).</li>
                                    <li>Ces enregistrements sont marqués <Text code>[FORCE-REPRISE]</Text> et devront être <strong>vérifiés et complétés manuellement</strong>.</li>
                                    <li>Les paiements dont le numéro de sinistre source est absent seront tout de même rejetés.</li>
                                </ul>
                            }
                            style={{ marginBottom: 20 }}
                        />
                        <div className="d-flex justify-content-end gap-2">
                            <Button onClick={() => setForceModalVisible(false)}>Annuler</Button>
                            <Button
                                style={{ backgroundColor: "#faad14", borderColor: "#faad14", color: "#fff" }}
                                onClick={() => setForceModalStep(2)}
                            >
                                Je comprends — Étape suivante →
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
                            <WarningOutlined style={{ fontSize: 52, color: "#ff4d4f" }} />
                            <Title level={4} style={{ marginTop: 12, marginBottom: 4, color: "#ff4d4f" }}>
                                Confirmation finale requise
                            </Title>
                        </div>
                        <Alert type="error" showIcon
                            message="Cette opération est irréversible"
                            description={
                                `Vous allez créer ${totalManquantsGlobal} enregistrement(s) avec données incomplètes, ` +
                                `marqués [FORCE-REPRISE]. Êtes-vous absolument certain de vouloir continuer ?`
                            }
                            style={{ marginBottom: 24 }}
                        />
                        <div className="d-flex justify-content-between">
                            <Button onClick={() => setForceModalStep(1)}>← Retour</Button>
                            <Button
                                danger type="primary"
                                icon={<ThunderboltOutlined />}
                                onClick={() => lancerImport(true)}
                            >
                                Oui — Lancer l'import en mode force
                            </Button>
                        </div>
                    </>
                )}
            </Modal>

            <style>{`
                .preview-row-warning td { background: #fff7ed !important; }
                .preview-row-warning:hover td { background: #fef3c7 !important; }
                .preview-row-immat td { background: #eff6ff !important; }
                .preview-row-immat:hover td { background: #dbeafe !important; }
            `}</style>
        </PageLayout>
    );
};

export default RepriseSinistresPaie;
